import React, { useState, useEffect } from 'react';
import './ListaChamadas.css';

function ListaChamadas({ onUpdateEstatisticas }) {
  const [chamadas, setChamadas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalResultados, setTotalResultados] = useState(0);
  const [filtros, setFiltros] = useState({
    dataInicio: new Date().toISOString().split('T')[0],
    horaInicio: '00:00',
    dataFim: new Date().toISOString().split('T')[0],
    horaFim: '23:59',
    centroCusto: '',
    ramal: '',
    status: ''
  });
  const [filtrosAplicados, setFiltrosAplicados] = useState({
    dataInicio: new Date().toISOString().split('T')[0],
    horaInicio: '00:00',
    dataFim: new Date().toISOString().split('T')[0],
    horaFim: '23:59',
    centroCusto: '',
    ramal: '',
    status: ''
  });
  const [paginaAtual, setPaginaAtual] = useState(0);
  const REGISTROS_POR_PAGINA = 25;
  const [centrosCusto, setCentrosCusto] = useState([]);

  const API_CONFIG = {
    baseURL: '/api',
    token: process.env.REACT_APP_API_TOKEN || '4a833f3d-9dfa-445c-afb0-7251bdf0e39d',
    usuario: process.env.REACT_APP_API_USER || 'dashboard',
    clientId: process.env.REACT_APP_CLIENT_ID || '174'
  };

  const formatarDataParaAPI = (data) => {
    const [ano, mes, dia] = data.split('-');
    return `${dia}/${mes}/${ano}`;
  };

  const formatarHoraParaAPI = (hora) => {
    return hora.substring(0, 5);
  };

  const isDestinoPadraoSistema = (destino) => {
    const destinosPadrao = ['FILA_ATENDIMENTO', 'AUDIO', 'URA'];
    return destinosPadrao.includes(destino?.toUpperCase());
  };

  const getChamadaStatus = (chamada) => {
    if (chamada.sip_code === '200' && !isDestinoPadraoSistema(chamada.destino)) {
      return 'atendida';
    }
    return 'nao_atendida';
  };

  const isRamalMatch = (origem, destino, filtroRamal) => {
    if (!filtroRamal) return true;
    
    // Se for apenas números, verifica se o ramal contém esses números em origem ou destino
    if (/^\d+$/.test(filtroRamal)) {
      return origem?.includes(filtroRamal) || destino?.includes(filtroRamal);
    }

    // Para padrão tipo "solutud-1025"
    const ramalPattern = /^([a-zA-Z]+)-(\d+)$/;
    const filtroMatch = filtroRamal.match(ramalPattern);
    
    if (!filtroMatch) return false;

    const [, prefixoFiltro, numeroFiltro] = filtroMatch;
    
    // Função auxiliar para verificar match em um número
    const verificarMatch = (numero) => {
      const numeroMatch = numero?.match(ramalPattern);
      if (!numeroMatch) return false;

      const [, prefixoNumero, numeroRamal] = numeroMatch;

      // Se o prefixo foi especificado, verifica se corresponde
      if (prefixoFiltro && prefixoFiltro !== '*') {
        return prefixoFiltro.toLowerCase() === prefixoNumero.toLowerCase() && 
               numeroRamal.includes(numeroFiltro);
      }

      // Se não especificou prefixo, verifica apenas o número
      return numeroRamal.includes(numeroFiltro);
    };

    // Verifica tanto origem quanto destino
    return verificarMatch(origem) || verificarMatch(destino);
  };

  const filtrarChamadas = (chamadas) => {
    return chamadas.filter(chamada => {
      // Filtro por status
      if (filtros.status && getChamadaStatus(chamada) !== filtros.status) {
        return false;
      }

      // Filtro por ramal (agora verificando origem e destino)
      if (filtros.ramal && !isRamalMatch(chamada.origem, chamada.destino, filtros.ramal)) {
        return false;
      }

      // Filtro por centro de custo
      if (filtros.centroCusto && chamada.centro_de_custo !== filtros.centroCusto) {
        return false;
      }

      return true;
    });
  };

  const isChamadaEfetuada = (chamada) => {
    // Debug para verificar os valores
    console.log('Verificando chamada:', {
      origem: chamada.origem,
      destino: chamada.destino,
      isOrigemRamal: /^([a-zA-Z]+-\d+|\d+)$/.test(chamada.origem),
      isDestinoValido: /^\d{8,}$/.test(chamada.destino?.replace(/\D/g, '')),
    });

    // Verifica se a origem é um ramal (padrão prefixo-número ou apenas número)
    const isOrigemRamal = /^([a-zA-Z]+-\d+|\d+)$/.test(chamada.origem);

    // Verifica se o destino tem pelo menos 8 dígitos
    const isDestinoValido = /^\d{8,}$/.test(chamada.destino?.replace(/\D/g, ''));

    return isOrigemRamal && isDestinoValido;
  };

  const calcularEstatisticas = (chamadas) => {
    return chamadas.reduce((stats, chamada) => {
      // Incrementa total de chamadas
      stats.total++;

      // Verifica se é chamada efetuada
      if (isChamadaEfetuada(chamada)) {
        stats.efetuadas++;
      }

      // Verifica se é chamada atendida (usando a lógica existente)
      if (getChamadaStatus(chamada) === 'atendida') {
        stats.atendidas++;
      } else {
        stats.naoAtendidas++;
      }

      return stats;
    }, {
      total: 0,
      efetuadas: 0,
      atendidas: 0,
      naoAtendidas: 0
    });
  };

  const buscarChamadas = async () => {
    try {
      setLoading(true);
      let todasChamadas = [];
      let posicaoInicial = 0;
      let totalRegistros = 0;
      let centrosCustoUnicos = new Set();
      
      do {
        const params = new URLSearchParams({
          cliente_id: API_CONFIG.clientId,
          data_inicial: formatarDataParaAPI(filtrosAplicados.dataInicio),
          hora_inicial: filtrosAplicados.horaInicio,
          data_final: formatarDataParaAPI(filtrosAplicados.dataFim),
          hora_final: filtrosAplicados.horaFim,
          did: '0',
          tronco_id: '0',
          centro_custo_id: '0',
          operador_id: '0',
          campanha_id: '0',
          sip_code: '0',
          pos_registro_inicial: posicaoInicial.toString()
        });

        const response = await fetch(`${API_CONFIG.baseURL}/listar_historico_chamada?${params}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Usuario': API_CONFIG.usuario,
            'Token': API_CONFIG.token,
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseText = await response.text();
        const data = JSON.parse(responseText);

        if (data && data.dados) {
          const chamadasFiltradas = filtrarChamadas(data.dados);
          todasChamadas = [...todasChamadas, ...chamadasFiltradas];
          totalRegistros = data.qtd_total_resultados;
          posicaoInicial += REGISTROS_POR_PAGINA;

          data.dados.forEach(chamada => {
            if (chamada.centro_de_custo) {
              centrosCustoUnicos.add(chamada.centro_de_custo);
            }
          });

          setChamadas(todasChamadas);
          setTotalResultados(todasChamadas.length);

          // Calcular e emitir estatísticas
          const estatisticas = calcularEstatisticas(todasChamadas);
          // Usando a prop desestruturada
          if (onUpdateEstatisticas) {
            onUpdateEstatisticas(estatisticas);
          }
        } else {
          throw new Error('Formato de dados inesperado');
        }

      } while (posicaoInicial < totalRegistros);

      const centrosCustoOrdenados = Array.from(centrosCustoUnicos).sort();
      setCentrosCusto(centrosCustoOrdenados);

    } catch (error) {
      console.error('Erro ao buscar chamadas:', error);
      setChamadas([]);
      setTotalResultados(0);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltrar = () => {
    setFiltrosAplicados(filtros);
  };

  useEffect(() => {
    buscarChamadas();
  }, [filtrosAplicados]);

  useEffect(() => {
    console.log('Chamadas atualizadas:', chamadas);
  }, [chamadas]);

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const LoadingComponent = () => {
    const progresso = totalResultados > 0 
      ? Math.round((chamadas.length / totalResultados) * 100)
      : 0;

    return (
      <div className="loading">
        <i className="fas fa-spinner fa-spin"></i>
        <div className="loading-info">
          <span>Carregando chamadas...</span>
          {totalResultados > 0 && (
            <div className="loading-progress">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${progresso}%` }}
                />
              </div>
              <span>{chamadas.length} de {totalResultados} registros</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="lista-container">
      <div className="lista-header">
        <div className="lista-titulo">
          <h2>Lista de Chamadas</h2>
          <span className="total-resultados">
            Total: {totalResultados} chamadas
          </span>
        </div>
        <div className="filtros-container">
          <div className="filtro-grupo filtro-grupo-data">
            <label>Data e Hora Inicial:</label>
            <div className="data-hora-inputs">
              <input
                type="date"
                name="dataInicio"
                value={filtros.dataInicio}
                onChange={handleFiltroChange}
                className="filtro-input"
              />
              <input
                type="time"
                name="horaInicio"
                value={filtros.horaInicio}
                onChange={handleFiltroChange}
                className="filtro-input hora-input"
              />
            </div>
          </div>

          <div className="filtro-grupo filtro-grupo-data">
            <label>Data e Hora Final:</label>
            <div className="data-hora-inputs">
              <input
                type="date"
                name="dataFim"
                value={filtros.dataFim}
                onChange={handleFiltroChange}
                className="filtro-input"
              />
              <input
                type="time"
                name="horaFim"
                value={filtros.horaFim}
                onChange={handleFiltroChange}
                className="filtro-input hora-input"
              />
            </div>
          </div>

          <div className="filtro-grupo">
            <label>Centro de Custo:</label>
            <select
              name="centroCusto"
              value={filtros.centroCusto}
              onChange={handleFiltroChange}
              className="filtro-input"
            >
              <option value="">Todos</option>
              {centrosCusto.map(centro => (
                <option key={centro} value={centro}>{centro}</option>
              ))}
            </select>
          </div>

          <div className="filtro-grupo">
            <label>Ramal:</label>
            <input
              type="text"
              name="ramal"
              value={filtros.ramal}
              onChange={handleFiltroChange}
              placeholder="Ex: exemplo-1000 ou 1000"
              className="filtro-input"
            />
          </div>

          <div className="filtro-grupo">
            <label>Status:</label>
            <select
              name="status"
              value={filtros.status}
              onChange={handleFiltroChange}
              className="filtro-input"
            >
              <option value="">Todos</option>
              <option value="atendida">Atendida</option>
              <option value="nao_atendida">Não Atendida</option>
            </select>
          </div>

          <div className="filtro-grupo filtro-acoes">
            <label>&nbsp;</label>
            <button 
              className="btn-filtrar"
              onClick={handleFiltrar}
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Filtrando...
                </>
              ) : (
                <>
                  <i className="fas fa-search"></i>
                  Filtrar
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="tabela-wrapper">
        {loading ? (
          <LoadingComponent />
        ) : (
          <table className="tabela-chamadas">
            <thead>
              <tr>
                <th>Data</th>
                <th>Hora</th>
                <th>Origem</th>
                <th>Destino</th>
                <th>Recurso</th>
                <th>Duração</th>
                <th>Centro de Custo</th>
                <th>Motivo Desligamento</th>
                <th>SIP Code</th>
                <th>Gravação</th>
              </tr>
            </thead>
            <tbody>
              {chamadas.map((chamada, index) => {
                const dataHora = chamada.data ? chamada.data.split(' ') : ['', ''];
                const [data, hora] = dataHora;
                const status = getChamadaStatus(chamada);
                
                return (
                  <tr key={chamada.chamada_id || index} className={`status-${status}`}>
                    <td>{data || '-'}</td>
                    <td>{hora || '-'}</td>
                    <td>{chamada.origem || '-'}</td>
                    <td>{chamada.destino || '-'}</td>
                    <td>{chamada.recurso || '-'}</td>
                    <td>{chamada.duracao || '-'}</td>
                    <td>{chamada.centro_de_custo || '-'}</td>
                    <td>{chamada.motivo_deligamento || '-'}</td>
                    <td>{chamada.sip_code || '-'}</td>
                    <td>
                      {chamada.link_gravacao && chamada.link_gravacao.length > 0 ? (
                        <button 
                          className="btn-gravacao"
                          onClick={() => window.open(chamada.link_gravacao[0], '_blank')}
                          title="Ouvir gravação"
                        >
                          <i className="fas fa-play"></i>
                        </button>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// Definindo as props padrão
ListaChamadas.defaultProps = {
  onUpdateEstatisticas: () => {} // Função vazia como fallback
};

export default ListaChamadas; 